import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  KeyRound, Plus, Trash2, Activity, Building2, X,
  Copy, Check, ShieldCheck, Clock, Database, Lock, ExternalLink,
} from 'lucide-react';
import SearchInput from '../components/SearchInput';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Administración de APIs (admin).
 * Fase 1: panel central de los tokens de integración (API de datos por cliente):
 * ver los activos con su uso, generar/rotar y revocar. Reutiliza los endpoints
 * existentes de StockApi. Los datasets seleccionables (pedidos, facturación, etc.)
 * llegan en fases siguientes.
 */
export default function AdminApis() {
  const { theme } = useTheme();
  const tkn = () => localStorage.getItem('token');

  const [apis, setApis]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [msg, setMsg]             = useState(null);
  const [search, setSearch]       = useState('');
  const [results, setResults]     = useState([]);
  const [generando, setGenerando] = useState(null);
  const [tokenNuevo, setTokenNuevo] = useState(null);   // resultado del POST
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [copiado, setCopiado]     = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [resolviendo, setResolviendo] = useState(null);

  const bgPrimary   = { backgroundColor: theme.primary };
  const bgPrimaryLt = { backgroundColor: theme.primaryLight };
  const textPrimary = { color: theme.primary };

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };
  const fechaCorta = (v) => v ? new Date(v).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const fetchApis = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/mis-clientes-con-token`, { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      setApis(Array.isArray(d.clientes) ? d.clientes : []);
    } catch { setApis([]); }
    finally { setLoading(false); }
  };
  const fetchSolicitudes = async () => {
    try {
      const r = await fetch(`${API_URL}/api/api-key-solicitudes?estado=pendiente`, { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      setSolicitudes(Array.isArray(d.solicitudes) ? d.solicitudes : []);
    } catch { setSolicitudes([]); }
  };
  useEffect(() => { fetchApis(); fetchSolicitudes(); }, []);

  const resolverSolicitud = async (sol, accion) => {
    setResolviendo(sol.id);
    try {
      const r = await fetch(`${API_URL}/api/api-key-solicitudes/${sol.id}/resolver`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'No se pudo resolver');
      showMsg(d.message || (accion === 'aprobar' ? 'Solicitud aprobada' : 'Solicitud rechazada'));
      fetchSolicitudes(); fetchApis();
    } catch (e) { showMsg(e.message || 'No se pudo resolver la solicitud', 'error'); }
    finally { setResolviendo(null); }
  };

  const buscar = async (val) => {
    setSearch(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try {
      const r = await fetch(`${API_URL}/api/clientes/search?query=${encodeURIComponent(val)}`, { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      setResults(d.clientes || []);
    } catch { setResults([]); }
  };

  const generar = async (cliente) => {
    setGenerando(cliente.id);
    try {
      const r = await fetch(`${API_URL}/api/clientes/${cliente.id}/stock-api-token`, {
        method: 'POST', headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'No se pudo generar');
      setTokenNuevo({ cliente, data: d });
      setSearch(''); setResults([]);
      fetchApis();
    } catch (e) { showMsg(e.message || 'No se pudo generar el token', 'error'); }
    finally { setGenerando(null); }
  };

  const revocar = async (cliente) => {
    try {
      const r = await fetch(`${API_URL}/api/clientes/${cliente.id}/stock-api-token`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tkn()}` },
      });
      if (!r.ok) throw new Error();
      showMsg(`Token de ${cliente.razon_social} revocado`);
      setConfirmRevoke(null); fetchApis();
    } catch { showMsg('No se pudo revocar el token', 'error'); }
  };

  const tokenPlano = tokenNuevo && (tokenNuevo.data?.token || tokenNuevo.data?.tokenPlano || null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Aviso */}
      {msg && (
        <div className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${msg.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {msg.text}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Cabecera */}
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2.5 rounded-xl" style={bgPrimaryLt}>
            <KeyRound className="w-6 h-6" style={textPrimary} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Administración de APIs</h1>
            <p className="text-sm text-gray-500">Tokens de integración para consumir datos desde herramientas externas (BI)</p>
          </div>
        </div>

        {/* Generar token */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Plus size={16} style={textPrimary} />
            <h2 className="font-semibold text-gray-800">Generar una API para un cliente</h2>
          </div>
          <div className="relative max-w-xl">
            <SearchInput
              value={search}
              onChange={(e) => buscar(e.target.value)}
              placeholder="Buscar clínica por nombre…"
              borderClass="border-2 border-gray-200"
              roundedClass="rounded-xl"
              blurColor="#e5e7eb"
              inputClassName="h-11"
            />
            {results.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                {results.map((c) => (
                  <button key={c.id} onClick={() => generar(c)} disabled={generando === c.id}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 disabled:opacity-50">
                    <span className="flex items-center gap-2 min-w-0">
                      <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate">{c.razon_social || c.alias || `#${c.id}`}</span>
                    </span>
                    <span className="text-xs font-semibold flex-shrink-0" style={textPrimary}>
                      {generando === c.id ? 'Generando…' : 'Generar'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
            <ShieldCheck size={13} /> Por seguridad, el token se muestra una sola vez y CentralSalud no lo conserva.
          </p>
        </div>

        {/* Solicitudes pendientes */}
        {solicitudes.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border-2 shadow-sm overflow-hidden" style={{ borderColor: theme.primary }}>
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2" style={bgPrimaryLt}>
              <Clock size={16} style={textPrimary} />
              <h2 className="font-semibold text-gray-800">Solicitudes de API Key</h2>
              <span className="text-xs font-bold text-white rounded-full px-2 py-0.5" style={bgPrimary}>{solicitudes.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {solicitudes.map((s) => (
                <div key={s.id} className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 flex items-center gap-2">
                      <Building2 size={14} className="text-gray-400" />
                      {s.razon_social}
                      {s.ya_tiene_token ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">YA TIENE TOKEN</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Solicitada {fechaCorta(s.creada_en)}
                      {s.solicitado_por_nombre && <> · por <strong>{s.solicitado_por_nombre}</strong></>}
                      {s.localidad && <> · {s.localidad}</>}
                    </div>
                    {s.motivo && <div className="text-xs text-gray-600 mt-1 italic">“{s.motivo}”</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => resolverSolicitud(s, 'aprobar')} disabled={resolviendo === s.id}
                      className="px-3 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                      style={bgPrimary}>
                      <Check size={13} /> {resolviendo === s.id ? 'Emitiendo…' : 'Aceptar y emitir'}
                    </button>
                    <button onClick={() => resolverSolicitud(s, 'rechazar')} disabled={resolviendo === s.id}
                      className="px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                      Rechazar
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-2.5 bg-gray-50 text-xs text-gray-500">
              Al aceptar se emite el token: el cliente lo reclama desde su panel “API Keys” (24 h).
            </div>
          </div>
        )}

        {/* APIs activas */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity size={16} style={textPrimary} />
              <h2 className="font-semibold text-gray-800">APIs activas</h2>
              <span className="text-xs font-bold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{apis.length}</span>
            </div>
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-4 border-gray-200" style={{ borderTopColor: theme.primary }} />
            </div>
          ) : apis.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">
              Todavía no hay ninguna API activa. Generá una para un cliente arriba.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-5 py-3 font-semibold">Cliente</th>
                    <th className="px-5 py-3 font-semibold">Creada</th>
                    <th className="px-5 py-3 font-semibold">Última llamada</th>
                    <th className="px-5 py-3 font-semibold text-right">Llamadas</th>
                    <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {apis.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 hover:bg-opacity-70">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-gray-800">{c.razon_social}</div>
                        <div className="text-xs text-gray-400">
                          {c.localidad || '—'}
                          {(c.clinica_matriz === 0 || c.clinica_matriz === null)
                            ? <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-xs font-bold" style={{ ...bgPrimaryLt, ...textPrimary }}>Matriz</span>
                            : <span className="ml-1.5 text-xs text-gray-400">Centro asociado</span>}
                          {c.gestion_stock === 'inactivo' && (
                            <span
                              className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200"
                              title="Stock inactivo: los 7 endpoints de la API de Stock responden 403 (inventario, lotes, historial, catálogo, código de barras, entradas y salidas). La API de Datos (BI) sí funciona con el mismo token."
                            >
                              Stock inactivo · solo Datos
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                        {fechaCorta(c.stock_api_token_created_at)}
                        {c.created_by_nombre && <div className="text-xs text-gray-400">por {c.created_by_nombre}</div>}
                      </td>
                      <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                        {c.ultima_llamada
                          ? <span className="inline-flex items-center gap-1"><Clock size={12} className="text-gray-400" />{fechaCorta(c.ultima_llamada)}</span>
                          : <span className="text-gray-400">Sin uso</span>}
                      </td>
                      <td className="px-5 py-3 text-right font-medium text-gray-700 tabular-nums">{Number(c.total_llamadas) || 0}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => generar(c)} disabled={generando === c.id}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50" style={bgPrimary}
                            title="Regenerar (rota el token; el cliente lo reclama de nuevo)">
                            {generando === c.id ? '…' : 'Regenerar'}
                          </button>
                          <button onClick={() => setConfirmRevoke(c)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title="Revocar">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Roadmap de datasets */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} style={textPrimary} />
            <h2 className="font-semibold text-gray-800">Datos que expone la API</h2>
          </div>
          {/* Los 10 datasets en 3 estados: abierto, premium (exige gestión de
              stock contratada) y sin-datos-todavía. */}
          <div className="flex flex-wrap gap-2">
            {[
              { t: 'Stock / inventario',       ep: 'inventario',     estado: 'premium' },
              { t: 'Lotes y caducidades',      ep: 'lotes',          estado: 'premium' },
              { t: 'Movimientos de stock',     ep: 'movimientos',    estado: 'premium' },
              { t: 'Catálogo de productos',    ep: 'catalogo',       estado: 'premium' },
              { t: 'Pedidos (líneas)',         ep: 'ventas-lineas',  estado: 'abierto' },
              { t: 'Pedidos (cabeceras)',      ep: 'pedidos',        estado: 'abierto' },
              { t: 'Facturación',              ep: 'facturacion',    estado: 'abierto' },
              { t: 'Clínicas',                 ep: 'clinicas',       estado: 'abierto' },
              { t: 'Proveedores',              ep: 'proveedores',    estado: 'abierto' },
              { t: 'Tiempos de entrega',       ep: 'estados-pedido', estado: 'abierto' },
            ].map((d) => {
              const cfg = {
                abierto:   { cls: 'bg-green-50 text-green-700 border-green-200', Icon: Check,
                             title: 'Disponible para cualquier token' },
                premium:   { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Lock,
                             title: 'Requiere gestión de stock contratada: devuelve 403 stock_disabled si está inactiva' },
                sin_datos: { cls: 'bg-gray-50 text-gray-500 border-gray-200',    Icon: Clock,
                             title: 'Disponible, pero todavía sin histórico: se puebla con los cambios de estado a partir de ahora' },
              }[d.estado];
              return (
                <span key={d.t} title={`GET /api/v1/bi/${d.ep} — ${cfg.title}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`}>
                  <cfg.Icon size={12} />{d.t}
                </span>
              );
            })}
          </div>
          <div className="text-xs text-gray-500 mt-3 space-y-1">
            <p>
              Los 10 datasets están operativos. Los marcados con <Lock size={11} className="inline mb-0.5" /> requieren
              que el cliente tenga la <strong>gestión de stock contratada</strong>; si está inactiva devuelven
              <code className="mx-1 px-1 rounded bg-gray-100">403 stock_disabled</code>.
              El resto responde siempre.
            </p>
            <p>
              <strong>Tiempos de entrega</strong> ya contiene histórico. Los eventos anteriores a la
              puesta en marcha se reconstruyeron a partir de la fecha de pedido y la de cierre, y van
              marcados con <code className="mx-1 px-1 rounded bg-gray-100">origen = backfill</code>:
              solo tienen apertura y cierre, sin los estados intermedios. Los nuevos se registran en
              vivo con <code className="mx-1 px-1 rounded bg-gray-100">origen = trigger</code>. Para
              métricas de precisión conviene filtrar por ese campo.
            </p>
            <p className="text-gray-400">
              En la próxima fase se podrá elegir por token qué datasets expone cada API Key.
            </p>
            <a href="/api-docs/datos" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold pt-1" style={textPrimary}>
              <ExternalLink size={12} /> Ver la documentación de los datasets
            </a>
          </div>
        </div>
      </div>

      {/* Modal: token generado */}
      {tokenNuevo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setTokenNuevo(null); setCopiado(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><KeyRound size={18} style={textPrimary} /> API generada</h3>
              <button onClick={() => { setTokenNuevo(null); setCopiado(false); }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3"><strong>{tokenNuevo.cliente.razon_social}</strong></p>

            {tokenPlano ? (
              <>
                <div className="bg-gray-900 rounded-xl p-3 flex items-center justify-between gap-2">
                  <code className="text-green-300 text-xs break-all">{tokenPlano}</code>
                  <button onClick={() => { navigator.clipboard?.writeText(tokenPlano); setCopiado(true); }}
                    className="flex-shrink-0 p-2 rounded-lg bg-white bg-opacity-10 text-white hover:bg-opacity-20" title="Copiar">
                    {copiado ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>
                <p className="text-xs text-red-600 mt-2 font-medium">⚠️ Copiá el token ahora: no se vuelve a mostrar.</p>
              </>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
                {tokenNuevo.data?.message || 'Token generado. El cliente matriz lo reclama desde su panel (por seguridad, CentralSalud no lo ve).'}
              </div>
            )}

            <div className="flex justify-end mt-5">
              <button onClick={() => { setTokenNuevo(null); setCopiado(false); }} className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90" style={bgPrimary}>Entendido</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: confirmar revocar */}
      {confirmRevoke && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmRevoke(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Revocar API</h3>
            <p className="text-sm text-gray-600">Vas a revocar el token de <strong>{confirmRevoke.razon_social}</strong>. Las integraciones que lo usen dejarán de funcionar de inmediato. ¿Continuar?</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmRevoke(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">Cancelar</button>
              <button onClick={() => revocar(confirmRevoke)} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Revocar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
